import { Component, Input, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AutoUnsubscribe } from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import { MatchedVccFilter } from '../../model/match-vcc';
import { Merchant } from 'src/app/dashboard/model/merchant-guid';
import { Subscription } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatchingVccService } from '../../service/matching-vcc.service';
import { MerchantsService } from 'src/infrastructure/merchants/service/merchants.service';

@AutoUnsubscribe()
@Component({
    selector: 'matching-vcc-filter',
    templateUrl: './matching-vcc-filter.component.html',
    standalone: false
})
export class MatchingVccFilterComponent implements OnInit {
  @Input() config!: MatchedVccFilter;

  matchingFilterForm!: FormGroup;

  merchantGuids: Merchant[] = [];

  breakPoint$?: Subscription;
  isSmall: boolean = false;

  constructor(
    @Optional() public matchingDialogRef: MatDialogRef<MatchingVccFilterComponent>,
    private formBuilder: FormBuilder,
    private merchantsService: MerchantsService,
    private matchingVccService: MatchingVccService,
    breakpointObserver: BreakpointObserver,
  ) {
    this.matchingFilterForm = this.formBuilder.group({
      releasedDateFrom: null,
      releasedDateTo: null,
      tramsBookingStartDateFrom: null,
      tramsBookingStartDateTo: null,
      tramsBookingEndDateFrom: null,
      tramsBookingEndDateTo: null,
      orderNumber: null,
      tramsInvoiceNumber: null,
      tramsBookingNo: null,
      merchantName: null,
      cardLastFour: null,
      merchantGuid: null,
      isMapped: null,
      isAmountMatches: null,
      isResolved: null,
    });

    this.breakPoint$ = breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall
    ]).subscribe(result => {
      this.isSmall = result.matches;
    });

  }

  ngOnInit(): void {
    this.matchingVccService.getMatchingFilter().subscribe((filter: MatchedVccFilter) => {
      this.config = filter || {};


      this.matchingFilterForm.patchValue({
        releasedDateFrom: this.config.releasedDateFrom || null,
        releasedDateTo: this.config.releasedDateTo || null,
        tramsBookingStartDateFrom: this.config.tramsBookingStartDateFrom || null,
        tramsBookingStartDateTo: this.config.tramsBookingStartDateTo || null,
        tramsBookingEndDateFrom: this.config.tramsBookingEndDateFrom || null,
        tramsBookingEndDateTo: this.config.tramsBookingEndDateTo || null,
        orderNumber: null,
        tramsInvoiceNumber: null,
        tramsBookingNo: null,
        merchantName: null,
        cardLastFour: this.config.cardLastFour || null,
        merchantGuid: this.config.merchantGuid || null,
        isMapped: this.config.isMapped || null,
        isAmountMatches: this.config.isAmountMatches || null,
        isResolved: this.config.isResolved || null
      });

      if (this.config?.orderNumber && typeof this.config.orderNumber === 'string' && this.config.orderNumber.trim()) {
        this.matchingFilterForm.get('orderNumber')?.setValue({ value: this.config.orderNumber });
      }

      if (this.config?.tramsInvoiceNumber && typeof this.config.tramsInvoiceNumber === 'string' && this.config.tramsInvoiceNumber.trim()) {
        this.matchingFilterForm.get('tramsInvoiceNumber')?.setValue({ value: this.config.tramsInvoiceNumber });
      }

      if (this.config?.tramsBookingNo && typeof this.config.tramsBookingNo === 'string' && this.config.tramsBookingNo.trim()) {
        this.matchingFilterForm.get('tramsBookingNo')?.setValue({ value: this.config.tramsBookingNo });
      }

      if (this.config?.merchantName && typeof this.config.merchantName === 'string' && this.config.merchantName.trim()) {
        this.matchingFilterForm.get('merchantName')?.setValue({ value: this.config.merchantName });
      }
    });

    this.merchantsService.merchants$.subscribe((data) => {
      this.merchantGuids = data;
    });
  }

  applyMatchingFilters() {
    const formValues = this.matchingFilterForm.value;

    this.config = { ...this.config, ...formValues };

    if (this.config.orderNumber !== null && this.config.orderNumber !== undefined) {
      if (typeof this.config.orderNumber === 'object' && this.config.orderNumber !== null) {
        this.config.orderNumber = (this.config.orderNumber as any)?.value || (this.config.orderNumber as any)?.id || '';
      } else if (typeof this.config.orderNumber === 'string') {
        this.config.orderNumber = this.config.orderNumber.trim();
      }
    }

    if (this.config.tramsInvoiceNumber !== null && this.config.tramsInvoiceNumber !== undefined) {
      if (typeof this.config.tramsInvoiceNumber === 'object' && this.config.tramsInvoiceNumber !== null) {
        this.config.tramsInvoiceNumber = (this.config.tramsInvoiceNumber as any)?.value || (this.config.tramsInvoiceNumber as any)?.id || '';
      } else if (typeof this.config.tramsInvoiceNumber === 'string') {
        this.config.tramsInvoiceNumber = this.config.tramsInvoiceNumber.trim();
      }
    }

    if (this.config.tramsBookingNo !== null && this.config.tramsBookingNo !== undefined) {
      if (typeof this.config.tramsBookingNo === 'object' && this.config.tramsBookingNo !== null) {
        this.config.tramsBookingNo = (this.config.tramsBookingNo as any)?.value || (this.config.tramsBookingNo as any)?.id || '';
      } else if (typeof this.config.tramsBookingNo === 'string') {
        this.config.tramsBookingNo = this.config.tramsBookingNo.trim();
      }
    }

    if (this.config.merchantName !== null && this.config.merchantName !== undefined) {
      if (typeof this.config.merchantName === 'object' && this.config.merchantName !== null) {
        const merchantObj = this.config.merchantName as any;
        let extractedValue = '';
        if (merchantObj.value && typeof merchantObj.value === 'string') {
          extractedValue = merchantObj.value;
        } else if (merchantObj.id && typeof merchantObj.id === 'string') {
          extractedValue = merchantObj.id;
        } else if (merchantObj.name && typeof merchantObj.name === 'string') {
          extractedValue = merchantObj.name;
        } else if (merchantObj.MerchantName && typeof merchantObj.MerchantName === 'string') {
          extractedValue = merchantObj.MerchantName;
        } else if (merchantObj.merchantName && typeof merchantObj.merchantName === 'string') {
          extractedValue = merchantObj.merchantName;
        } else if (merchantObj.text && typeof merchantObj.text === 'string') {
          extractedValue = merchantObj.text;
        } else if (merchantObj.label && typeof merchantObj.label === 'string') {
          extractedValue = merchantObj.label;
        } else {
          extractedValue = '';
          console.warn('Could not extract string value from merchant name object:', merchantObj);
        }

        this.config.merchantName = extractedValue.trim();
      } else if (typeof this.config.merchantName === 'string') {
        this.config.merchantName = this.config.merchantName.trim();
      }
    }

    if (this.config.cardLastFour && typeof this.config.cardLastFour === 'string') {
      this.config.cardLastFour = this.config.cardLastFour.trim();
    }

    this.matchingVccService.setMatchingFilter(this.config);

    this.matchingDialogRef.close(this.config);
  }

  handleBookingFiltersFormCloseEvent() {
    if (this.matchingDialogRef) {
      this.matchingDialogRef.close(null);
    }
  }

  clearMatchingFields(...args: string[]) {
    if (!args.length) {
      this.matchingFilterForm.reset();
      this.config = {};
      this.matchingVccService.setMatchingFilter({});
      return;
    }
    args.forEach((field) => {
      this.matchingFilterForm.controls[field].reset();
      if (this.config && this.config[field] !== undefined) {
        this.config[field] = null;
      }
    });
  }
}
